/**
 * boundaryMath.ts — Module tính toán hình học không gian và góc phương vị mặt tiền
 * Dành cho workflow định vị 4 góc ranh giới nghĩa trang, xác định cạnh mặt tiền/hướng cổng
 * và scale/rotate mô hình 3D GLB khớp tọa độ thực tế.
 *
 * Tương thích hoàn toàn với:
 * - Bảng DB: `cemetery.scene_geo_reference` (V21)
 * - Module Java backend: `SceneGeoTransform.java` (DEC-034 / Spec S12.2 §5.5)
 * - Three.js WebGL scene: Y-up, East = +X, North = -Z
 */

/** Hằng số hình cầu cho vĩ độ — Spec S12.2 §5.5 và SceneGeoTransform.java */
export const METERS_PER_DEGREE_LATITUDE = 110574.0;

/** Hằng số hình cầu cho kinh độ tại xích đạo (nhân cos(lat) khi dùng) */
export const METERS_PER_DEGREE_LONGITUDE_AT_EQUATOR = 111320.0;

/** Bán kính Trái Đất trung bình (mét) cho công thức Haversine */
export const EARTH_RADIUS_METERS = 6371008.8;

/** Ngưỡng sai số khứ hồi độ (DEC-034 / Spec §5.5 điều 3) */
export const ROUND_TRIP_TOLERANCE_DEGREES = 1e-9;

/** Sàn cos(lat) tránh suy biến tại cực */
export const MIN_COS_LATITUDE = 1e-6;

/** Điểm tọa độ WGS84 */
export interface GeoPoint {
  lat: number;
  lng: number;
  lon?: number;
}

/** Điểm tọa độ không gian Scene 3D (mặt phẳng mặt đất XZ) */
export interface ScenePoint {
  x: number;
  z: number;
  y?: number;
}

/** Vector 2D mặt phẳng Đông - Bắc (mét) */
export interface Vector2D {
  east: number;
  north: number;
}

/** Cạnh ranh giới giữa 2 đỉnh */
export interface BoundaryEdge {
  index: number;
  from: GeoPoint;
  to: GeoPoint;
  lengthM: number;
  bearingDeg: number;
}

/** Kết quả phân tích toàn diện thửa đất 4 góc */
export interface BoundaryAnalysis {
  vertices: GeoPoint[];
  edges: BoundaryEdge[];
  perimeterM: number;
  areaM2: number;
  centroid: GeoPoint;
  frontageEdgeIndex: number;
  frontageEdge: BoundaryEdge;
  frontageBearingDeg: number;
  outwardNormalBearingDeg: number;
  outwardNormalVector: Vector2D;
  dimensions: {
    widthM: number;
    depthM: number;
  };
}

/** Cấu hình tương thích schema bảng `cemetery.scene_geo_reference` */
export interface SceneGeoReferenceConfig {
  originLon: number;
  originLat: number;
  originElevationM?: number;
  bearingDeg: number;
  metersPerSceneUnit: number;
  axisOrder: "LON_LAT" | "LonLat";
  handedness: "RIGHT_HANDED_Y_UP" | "RightHanded";
  canonicalSrid: number;
  sourceSrid: number;
  status: "DRAFT" | "VERIFIED";
  source: string;
}

/**
 * Chuẩn hóa toạ độ đầu vào thành GeoPoint chuẩn { lat, lng }
 */
export function toGeoPoint(
  input: GeoPoint | [number, number] | { lat: number; lon: number }
): GeoPoint {
  if (Array.isArray(input)) {
    // Quy ước Leaflet [lat, lng]
    return { lat: input[0], lng: input[1], lon: input[1] };
  }
  const lng = "lng" in input ? input.lng : input.lon;
  return { lat: input.lat, lng, lon: lng };
}

/**
 * Chuẩn hóa góc phương vị (bearing) về khoảng [0, 360) độ
 */
export function normalizeBearing(deg: number): number {
  const mod = deg % 360;
  const normalized = mod < 0 ? mod + 360 : mod;
  return normalized === 0 ? 0 : normalized;
}

/**
 * Số mét trên một độ kinh tuyến tại vĩ độ lat
 */
export function localMetersPerDegreeLon(lat: number): number {
  const cosLat = Math.cos((lat * Math.PI) / 180);
  if (Math.abs(cosLat) < MIN_COS_LATITUDE) {
    throw new Error(`Vĩ độ ${lat} quá gần cực — kinh tuyến suy biến.`);
  }
  return METERS_PER_DEGREE_LONGITUDE_AT_EQUATOR * cosLat;
}

/**
 * Tính khoảng cách đại viên (Great Circle Distance) theo công thức Haversine (mét)
 */
export function calculateHaversineDistance(p1: GeoPoint, p2: GeoPoint): number {
  const pt1 = toGeoPoint(p1);
  const pt2 = toGeoPoint(p2);

  const radLat1 = (pt1.lat * Math.PI) / 180;
  const radLat2 = (pt2.lat * Math.PI) / 180;
  const dLat = ((pt2.lat - pt1.lat) * Math.PI) / 180;
  const dLng = ((pt2.lng - pt1.lng) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Tính phương vị ban đầu (initial bearing / forward azimuth) từ p1 đến p2 (độ, [0, 360))
 * 0° = Bắc, 90° = Đông, 180° = Nam, 270° = Tây (theo chiều kim đồng hồ).
 */
export function calculateBearing(from: GeoPoint, to: GeoPoint): number {
  const pt1 = toGeoPoint(from);
  const pt2 = toGeoPoint(to);

  const lat1 = (pt1.lat * Math.PI) / 180;
  const lat2 = (pt2.lat * Math.PI) / 180;
  const dLng = ((pt2.lng - pt1.lng) * Math.PI) / 180;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return normalizeBearing(bearing);
}

/**
 * Đổi điểm WGS84 sang toạ độ mét cục bộ (East, North) so với gốc origin
 */
export function geoToLocalMeters(pt: GeoPoint, origin: GeoPoint): { x: number; y: number } {
  const p = toGeoPoint(pt);
  const o = toGeoPoint(origin);
  const mLon = localMetersPerDegreeLon(o.lat);
  const x = (p.lng - o.lng) * mLon;
  const y = (p.lat - o.lat) * METERS_PER_DEGREE_LATITUDE;
  return { x, y };
}

/**
 * Đổi toạ độ mét cục bộ (East, North) về điểm WGS84 so với gốc origin
 */
export function localMetersToGeo(local: { x: number; y: number }, origin: GeoPoint): GeoPoint {
  const o = toGeoPoint(origin);
  const mLon = localMetersPerDegreeLon(o.lat);
  const lng = o.lng + local.x / mLon;
  const lat = o.lat + local.y / METERS_PER_DEGREE_LATITUDE;
  return { lat, lng, lon: lng };
}

/**
 * Tính chiều dài 4 cạnh thửa đất (mét)
 */
export function calculateEdgeDistances(points: GeoPoint[]): number[] {
  if (points.length < 2) return [];
  const distances: number[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const nextIdx = (i + 1) % n;
    const ptI = points[i];
    const ptNext = points[nextIdx];
    if (ptI && ptNext) {
      distances.push(calculateHaversineDistance(ptI, ptNext));
    }
  }
  return distances;
}

/**
 * Tính diện tích đa giác (m²) bằng công thức Shoelace trên toạ độ mét phẳng cục bộ
 */
export function calculatePolygonArea(points: GeoPoint[]): number {
  if (points.length < 3) return 0;
  const origin = points[0];
  if (!origin) return 0;
  const locals = points.map((p) => geoToLocalMeters(p, origin));
  const n = locals.length;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const curr = locals[i];
    const next = locals[(i + 1) % n];
    if (curr && next) {
      sum += curr.x * next.y - next.x * curr.y;
    }
  }
  return Math.abs(sum / 2);
}

/**
 * Tính toạ độ trọng tâm (centroid) đa giác thửa đất theo công thức polygon centroid
 */
export function calculateCentroid(points: GeoPoint[]): GeoPoint {
  if (points.length === 0) {
    throw new Error("Không thể tính trọng tâm từ mảng điểm rỗng");
  }
  if (points.length < 3) {
    // Nếu chỉ có 1 hoặc 2 điểm, trả về trung bình số học
    const sumLat = points.reduce((acc, p) => acc + p.lat, 0);
    const sumLng = points.reduce((acc, p) => acc + (p.lng ?? p.lon ?? 0), 0);
    const lat = sumLat / points.length;
    const lng = sumLng / points.length;
    return { lat, lng, lon: lng };
  }

  const origin = points[0];
  if (!origin) {
    throw new Error("Không thể tính trọng tâm từ mảng điểm rỗng");
  }
  const locals = points.map((p) => geoToLocalMeters(p, origin));
  const n = locals.length;

  let areaFactor = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < n; i++) {
    const curr = locals[i];
    const next = locals[(i + 1) % n];
    if (curr && next) {
      const cross = curr.x * next.y - next.x * curr.y;
      areaFactor += cross;
      cx += (curr.x + next.x) * cross;
      cy += (curr.y + next.y) * cross;
    }
  }

  // Trường hợp đa giác suy biến (các điểm thẳng hàng hoặc trùng nhau)
  if (Math.abs(areaFactor) < 1e-6) {
    const avgX = locals.reduce((acc, p) => acc + p.x, 0) / n;
    const avgY = locals.reduce((acc, p) => acc + p.y, 0) / n;
    return localMetersToGeo({ x: avgX, y: avgY }, origin);
  }

  const signedArea = areaFactor / 2;
  const localCentroid = {
    x: cx / (6 * signedArea),
    y: cy / (6 * signedArea),
  };

  return localMetersToGeo(localCentroid, origin);
}

/**
 * Tính vector pháp tuyến hướng ra ngoài (outward normal) và góc bearing của cạnh mặt tiền
 * Độc lập với thứ tự cắm mốc CW (thuận chiều kim đồng hồ) hay CCW (ngược chiều kim đồng hồ).
 */
export function calculateOutwardNormal(
  points: GeoPoint[],
  frontageEdgeIndex: number
): { vector: Vector2D; bearingDeg: number } {
  const n = points.length;
  if (n < 3) {
    throw new Error("Cần tối thiểu 3 điểm để xác định hướng outward normal");
  }
  if (frontageEdgeIndex < 0 || frontageEdgeIndex >= n) {
    throw new Error(`frontageEdgeIndex ${frontageEdgeIndex} ngoài phạm vi [0, ${n - 1}]`);
  }

  const centroid = calculateCentroid(points);
  const pA = points[frontageEdgeIndex];
  const pB = points[(frontageEdgeIndex + 1) % n];
  if (!pA || !pB) {
    throw new Error(`Điểm cạnh mặt tiền ${frontageEdgeIndex} không tồn tại`);
  }

  // Chuyển sang toạ độ mét so với centroid
  const localA = geoToLocalMeters(pA, centroid);
  const localB = geoToLocalMeters(pB, centroid);

  // Vector cạnh (East, North)
  const dx = localB.x - localA.x;
  const dy = localB.y - localA.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) {
    throw new Error("Hai điểm cạnh mặt tiền trùng nhau, không xác định được vector pháp tuyến");
  }

  // Hai ứng viên pháp tuyến đơn vị:
  // n1 (bên phải cạnh): (dy/len, -dx/len)
  // n2 (bên trái cạnh): (-dy/len, dx/len)
  const n1 = { east: dy / len, north: -dx / len };

  // Trung điểm cạnh mặt tiền so với centroid (local)
  const midLocal = {
    x: (localA.x + localB.x) / 2,
    y: (localA.y + localB.y) / 2,
  };

  // Vector từ centroid ra trung điểm cạnh là hướng ra ngoài thửa đất
  // Outward normal phải có tích vô hướng dương với midLocal (hướng ra xa tâm)
  const dot1 = n1.east * midLocal.x + n1.north * midLocal.y;
  const normal = dot1 >= 0 ? n1 : { east: -n1.east, north: -n1.north };

  // Góc bearing của vector pháp tuyến (độ, tính từ Bắc thật theo chiều kim đồng hồ)
  // azimuth = atan2(East, North)
  const bearing = (Math.atan2(normal.east, normal.north) * 180) / Math.PI;
  const bearingDeg = normalizeBearing(bearing);

  return { vector: normal, bearingDeg };
}

/**
 * Sinh toạ độ điểm bắt đầu và kết thúc của mũi tên hướng cổng (Gate Arrow)
 * dùng để vẽ Polyline / Arrowhead trực tiếp trên bản đồ Leaflet.
 */
export function calculateGateArrow(
  frontageEdge: BoundaryEdge,
  outwardNormalBearingDeg: number,
  arrowLengthM: number = 20
): { start: GeoPoint; end: GeoPoint } {
  const pA = toGeoPoint(frontageEdge.from);
  const pB = toGeoPoint(frontageEdge.to);

  // Trung điểm cạnh mặt tiền
  const midLocal = {
    x: (localMetersPerDegreeLon(pA.lat) * (pB.lng - pA.lng)) / 2,
    y: (METERS_PER_DEGREE_LATITUDE * (pB.lat - pA.lat)) / 2,
  };
  const start = localMetersToGeo(midLocal, pA);

  // Vector hướng outward theo bearingDeg
  const rad = (outwardNormalBearingDeg * Math.PI) / 180;
  const vectorEastM = Math.sin(rad) * arrowLengthM;
  const vectorNorthM = Math.cos(rad) * arrowLengthM;

  const end = localMetersToGeo({ x: vectorEastM, y: vectorNorthM }, start);

  return { start, end };
}

/**
 * Phân tích hình học toàn diện 4 góc ranh giới nghĩa trang
 */
export function analyzeBoundary(
  points: GeoPoint[],
  frontageEdgeIndex: number = 0
): BoundaryAnalysis {
  if (points.length < 3) {
    throw new Error("Ranh giới nghĩa trang yêu cầu tối thiểu 3 điểm mốc (chuẩn 4 điểm)");
  }

  const n = points.length;
  const normalizedPoints = points.map(toGeoPoint);
  const edges: BoundaryEdge[] = [];
  let perimeterM = 0;

  for (let i = 0; i < n; i++) {
    const from = normalizedPoints[i];
    const to = normalizedPoints[(i + 1) % n];
    if (from && to) {
      const lengthM = calculateHaversineDistance(from, to);
      const bearingDeg = calculateBearing(from, to);
      perimeterM += lengthM;
      edges.push({ index: i, from, to, lengthM, bearingDeg });
    }
  }

  const areaM2 = calculatePolygonArea(normalizedPoints);
  const centroid = calculateCentroid(normalizedPoints);
  const validatedFrontageIndex = (frontageEdgeIndex % n + n) % n;
  const frontageEdge = edges[validatedFrontageIndex] ?? edges[0];
  if (!frontageEdge) {
    throw new Error("Không tìm thấy cạnh mặt tiền hợp lệ");
  }
  const frontageBearingDeg = frontageEdge.bearingDeg;

  const { vector: outwardNormalVector, bearingDeg: outwardNormalBearingDeg } =
    calculateOutwardNormal(normalizedPoints, validatedFrontageIndex);

  // Kích thước thửa đất (width theo mặt tiền, depth theo chiều sâu vuông góc)
  const widthM = frontageEdge.lengthM;

  // Tính chiều sâu: khoảng cách cực đại từ các đỉnh tới đường thẳng chứa cạnh mặt tiền
  const origin = frontageEdge.from;
  const locals = normalizedPoints.map((p) => geoToLocalMeters(p, origin));
  // Inward normal vector = -outwardNormalVector
  const inwardNormal = {
    x: -outwardNormalVector.east,
    y: -outwardNormalVector.north,
  };
  let maxDepth = 0;
  for (const pt of locals) {
    const depth = pt.x * inwardNormal.x + pt.y * inwardNormal.y;
    if (depth > maxDepth) {
      maxDepth = depth;
    }
  }

  return {
    vertices: normalizedPoints,
    edges,
    perimeterM,
    areaM2,
    centroid,
    frontageEdgeIndex: validatedFrontageIndex,
    frontageEdge,
    frontageBearingDeg,
    outwardNormalBearingDeg,
    outwardNormalVector,
    dimensions: {
      widthM,
      depthM: maxDepth > 0 ? maxDepth : perimeterM / 4,
    },
  };
}

/**
 * Xây dựng đối tượng cấu hình tương thích bảng `cemetery.scene_geo_reference`
 */
export function buildSceneGeoReference(
  analysis: BoundaryAnalysis,
  options?: {
    originMode?: "centroid" | "frontage_midpoint";
    metersPerSceneUnit?: number;
    modelBoundingBox?: { x: number; y: number; z: number };
  }
): SceneGeoReferenceConfig {
  let originLon = analysis.centroid.lng;
  let originLat = analysis.centroid.lat;

  if (options?.originMode === "frontage_midpoint") {
    const arrow = calculateGateArrow(analysis.frontageEdge, analysis.outwardNormalBearingDeg, 0);
    originLon = arrow.start.lng;
    originLat = arrow.start.lat;
  }

  let metersPerSceneUnit = options?.metersPerSceneUnit ?? 1.0;
  if (options?.modelBoundingBox && !options?.metersPerSceneUnit) {
    // Nếu truyền model bounding box (kích thước scene units của GLB),
    // tính tỷ lệ scale sao cho chiều rộng model khớp bề ngang mặt tiền đất
    const modelWidth = Math.max(options.modelBoundingBox.x, 0.001);
    metersPerSceneUnit = analysis.dimensions.widthM / modelWidth;
  }

  return {
    originLon,
    originLat,
    bearingDeg: analysis.outwardNormalBearingDeg,
    metersPerSceneUnit,
    axisOrder: "LON_LAT",
    handedness: "RIGHT_HANDED_Y_UP",
    canonicalSrid: 4326,
    sourceSrid: 4326,
    status: "VERIFIED",
    source: "MANUAL_4CORNERS_GEOREF",
  };
}

/**
 * Chuyển đổi toạ độ Scene 3D → Địa lý WGS84 (forward affine)
 * Khớp chuẩn công thức của SceneGeoTransform.java (DEC-034):
 *
 *   e = x · metersPerSceneUnit      (mét, hướng Đông scene)
 *   n = −z · metersPerSceneUnit     (mét, hướng Bắc scene)
 *   E =  e · cos θ + n · sin θ      (mét, Đông thật)
 *   N = −e · sin θ + n · cos θ      (mét, Bắc thật)
 */
export function sceneToGeo(
  scenePoint: ScenePoint,
  ref: Pick<SceneGeoReferenceConfig, "originLon" | "originLat" | "bearingDeg" | "metersPerSceneUnit">
): GeoPoint {
  const thetaRad = (ref.bearingDeg * Math.PI) / 180;
  const cosTheta = Math.cos(thetaRad);
  const sinTheta = Math.sin(thetaRad);

  const east = scenePoint.x * ref.metersPerSceneUnit;
  const north = -scenePoint.z * ref.metersPerSceneUnit;

  const eastTrue = east * cosTheta + north * sinTheta;
  const northTrue = -east * sinTheta + north * cosTheta;

  const mLon = localMetersPerDegreeLon(ref.originLat);
  const lon = ref.originLon + eastTrue / mLon;
  const lat = ref.originLat + northTrue / METERS_PER_DEGREE_LATITUDE;

  return { lat, lng: lon, lon };
}

/**
 * Chuyển đổi toạ độ Địa lý WGS84 → Scene 3D (inverse affine)
 * Nghịch đảo đại số đúng tuyệt đối của sceneToGeo.
 */
export function geoToScene(
  geoPoint: GeoPoint,
  ref: Pick<SceneGeoReferenceConfig, "originLon" | "originLat" | "bearingDeg" | "metersPerSceneUnit">
): ScenePoint {
  const pt = toGeoPoint(geoPoint);
  const thetaRad = (ref.bearingDeg * Math.PI) / 180;
  const cosTheta = Math.cos(thetaRad);
  const sinTheta = Math.sin(thetaRad);

  const mLon = localMetersPerDegreeLon(ref.originLat);
  const eastTrue = (pt.lng - ref.originLon) * mLon;
  const northTrue = (pt.lat - ref.originLat) * METERS_PER_DEGREE_LATITUDE;

  const east = eastTrue * cosTheta - northTrue * sinTheta;
  const north = eastTrue * sinTheta + northTrue * cosTheta;

  const x = east / ref.metersPerSceneUnit;
  const z = -north / ref.metersPerSceneUnit;

  return { x, z, y: 0 };
}

/**
 * Đo sai số khứ hồi (round-trip error) giữa Scene → Geo → Scene → Geo (đơn vị độ)
 * Ngưỡng an toàn theo DEC-034 là < 1e-9 độ.
 */
export function roundTripDeviationDegrees(
  scenePoint: ScenePoint,
  ref: Pick<SceneGeoReferenceConfig, "originLon" | "originLat" | "bearingDeg" | "metersPerSceneUnit">
): number {
  const first = sceneToGeo(scenePoint, ref);
  const back = geoToScene(first, ref);
  const second = sceneToGeo(back, ref);

  return Math.max(Math.abs(second.lng - first.lng), Math.abs(second.lat - first.lat));
}

/**
 * Sinh ma trận chuyển đổi 4x4 (Three.js column-major 16-element array)
 * Phục vụ trực tiếp task Three.js của threejs-dev: Matrix4.fromArray(...)
 */
export function getSceneGeoMatrix4(
  ref: Pick<SceneGeoReferenceConfig, "bearingDeg" | "metersPerSceneUnit">,
  options?: {
    translation?: { x: number; y: number; z: number };
    scaleMultiplier?: number;
  }
): number[] {
  const scale = ref.metersPerSceneUnit * (options?.scaleMultiplier ?? 1.0);
  const thetaRad = (ref.bearingDeg * Math.PI) / 180;
  const cosT = Math.cos(thetaRad);
  const sinT = Math.sin(thetaRad);

  const tx = options?.translation?.x ?? 0;
  const ty = options?.translation?.y ?? 0;
  const tz = options?.translation?.z ?? 0;

  // Column-major array cho Three.js:
  // Cột 0: [scale * cosT, 0, -scale * sinT, 0]
  // Cột 1: [0, scale, 0, 0]
  // Cột 2: [scale * sinT, 0, scale * cosT, 0]
  // Cột 3: [tx, ty, tz, 1]
  return [
    scale * cosT,
    0,
    -scale * sinT,
    0,

    0,
    scale,
    0,
    0,

    scale * sinT,
    0,
    scale * cosT,
    0,

    tx,
    ty,
    tz,
    1,
  ];
}
